import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/layout/DashboardLayout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Landing from "@/pages/Landing";
import Onboarding from "@/pages/Onboarding";
import { homeForRole } from "@/lib/roles";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Orders from "@/pages/Orders";
import Tables from "@/pages/Tables";
import MenuManage from "@/pages/MenuManage";
import Inventory from "@/pages/Inventory";
import Vendors from "@/pages/Vendors";
import Customers from "@/pages/Customers";
import Reservations from "@/pages/Reservations";
import Reports from "@/pages/Reports";
import SettingsPage from "@/pages/Settings";
import Staff from "@/pages/Staff";
import Attendance from "@/pages/Attendance";
import Tasks from "@/pages/Tasks";
import Channels from "@/pages/Channels";
import KDS from "@/pages/KDS";
import Delivery from "@/pages/Delivery";
import Reviews from "@/pages/Reviews";
import Waste from "@/pages/Waste";
import WaiterPortal from "@/pages/WaiterPortal";
import KitchenPortal from "@/pages/KitchenPortal";
import Approvals from "@/pages/Approvals";
import Calls from "@/pages/Calls";
import Tokens from "@/pages/Tokens";
import Reconcile from "@/pages/Reconcile";
import SmsCenter from "@/pages/SmsCenter";
import Chain from "@/pages/Chain";
import Accounting from "@/pages/Accounting";
import Printers from "@/pages/Printers";
import WhatsAppCenter from "@/pages/WhatsAppCenter";
import Brands from "@/pages/Brands";
import AggregatorBoard from "@/pages/AggregatorBoard";
import Mess from "@/pages/Mess";
import DayEnd from "@/pages/DayEnd";
import Transfers from "@/pages/Transfers";
import PurchaseInvoices from "@/pages/PurchaseInvoices";
import Permissions from "@/pages/Permissions";
import FloorSetup from "@/pages/FloorSetup";
import Subscription from "@/pages/Subscription";
import ScanOrder from "@/pages/ScanOrder";
import Kiosk from "@/pages/Kiosk";
import TokenBoard from "@/pages/TokenBoard";
import FeedbackPublic from "@/pages/FeedbackPublic";
import Storefront from "@/pages/customer/Storefront";
import PaymentResult from "@/pages/customer/PaymentResult";

function LoadingScreen() {
  return <div className="h-screen flex items-center justify-center text-neutral-400 font-display">Loading ojent…</div>;
}

function needsOnboarding(user) {
  return user && user.role === "admin" && user.restaurant && !user.restaurant.onboarding_complete;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (needsOnboarding(user)) return <Navigate to="/onboarding" replace />;
  if (user) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function RootShell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) {
    if (location.pathname !== "/") return <Navigate to="/" replace />;
    return <Landing />;
  }
  if (needsOnboarding(user)) return <Navigate to="/onboarding" replace />;
  return <DashboardLayout />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/signup" element={<GuestOnly><Signup /></GuestOnly>} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/r/:slug" element={<Storefront />} />
            <Route path="/r/:slug/order" element={<Storefront />} />
            <Route path="/r/:slug/website" element={<Storefront />} />
            <Route path="/r/:slug/kiosk" element={<Kiosk />} />
            <Route path="/r/:slug/tokens" element={<TokenBoard />} />
            <Route path="/r/:slug/scan/:tableId" element={<ScanOrder />} />
            <Route path="/r/:slug/feedback" element={<FeedbackPublic />} />
            <Route path="/order" element={<Storefront />} />
            <Route path="/website" element={<Storefront />} />
            <Route path="/kiosk" element={<Kiosk />} />
            <Route path="/tokens" element={<TokenBoard />} />
            <Route path="/scan/:tableId" element={<ScanOrder />} />
            <Route path="/feedback" element={<FeedbackPublic />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route path="/" element={<RootShell />}>
              <Route index element={<Dashboard />} />
              <Route path="waiter" element={<WaiterPortal />} />
              <Route path="kitchen" element={<KitchenPortal />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="calls" element={<Calls />} />
              <Route path="tokens-desk" element={<Tokens />} />
              <Route path="reconcile" element={<Reconcile />} />
              <Route path="day-end" element={<DayEnd />} />
              <Route path="sms" element={<SmsCenter />} />
              <Route path="chain" element={<Chain />} />
              <Route path="accounting" element={<Accounting />} />
              <Route path="printers" element={<Printers />} />
              <Route path="whatsapp" element={<WhatsAppCenter />} />
              <Route path="brands" element={<Brands />} />
              <Route path="aggregator" element={<AggregatorBoard />} />
              <Route path="mess" element={<Mess />} />
              <Route path="transfers" element={<Transfers />} />
              <Route path="purchase-invoices" element={<PurchaseInvoices />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="floor-setup" element={<FloorSetup />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="pos" element={<POS />} />
              <Route path="kds" element={<KDS />} />
              <Route path="orders" element={<Orders />} />
              <Route path="tables" element={<Tables />} />
              <Route path="menu" element={<MenuManage />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="waste" element={<Waste />} />
              <Route path="vendors" element={<Vendors />} />
              <Route path="customers" element={<Customers />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="reservations" element={<Reservations />} />
              <Route path="channels" element={<Channels />} />
              <Route path="delivery" element={<Delivery />} />
              <Route path="reports" element={<Reports />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="staff" element={<Staff />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
